import React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import FormatPaintOutlinedIcon from "@mui/icons-material/FormatPaintOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

const SERVICES = [
  {
    title: "Selling & Buying",
    description: "Selling and buying of land, plots, flats and villas.",
    Icon: HandshakeOutlinedIcon,
    iconBg: "#F3E8FF",
    iconColor: "#7B1FA2",
    image:
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Construction",
    description: "Construction solutions for residential and commercial spaces.",
    Icon: EngineeringOutlinedIcon,
    iconBg: "#E3F2FD",
    iconColor: "#1565C0",
    image:
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Renovation",
    description:
      "Transforming existing spaces through quality renovation solutions.",
    Icon: FormatPaintOutlinedIcon,
    iconBg: "#E8F5E9",
    iconColor: "#2E7D32",
    image:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Channel Partners",
    description: "Working with builders and developers across PAN India.",
    Icon: GroupsOutlinedIcon,
    iconBg: "#FCE4EC",
    iconColor: "#C2185B",
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Developing Layouts & Marketing",
    description:
      "Developing layouts and providing professional property marketing solutions.",
    Icon: PlaceOutlinedIcon,
    iconBg: "#E8EAF6",
    iconColor: "#3949AB",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80",
  },
];

const ServiceCard = ({ service, index, accent, onLearnMore }) => {
  const { title, description, Icon, iconBg, iconColor, image } = service;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        bgcolor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 8px 28px rgba(40, 20, 60, 0.08)",
        overflow: "hidden",
        width: "100%",
        minHeight: { xs: 200, sm: 220 },
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(40, 20, 60, 0.12)",
        },
      }}
    >
      {/* Text column */}
      <Box
        sx={{
          flex: "1 1 52%",
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: iconBg,
            color: iconColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.25,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: accent,
            mb: 0.4,
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </Typography>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.98rem", sm: "1.05rem" },
            color: "#1A1A2E",
            mb: 0.75,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
            color: "#6B7280",
            lineHeight: 1.55,
            mb: 1.5,
            flexGrow: 1,
          }}
        >
          {description}
        </Typography>

        <Button
          onClick={onLearnMore}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          sx={{
            alignSelf: "flex-start",
            p: 0,
            minWidth: 0,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13,
            color: accent,
            "&:hover": {
              bgcolor: "transparent",
              color: accent,
              opacity: 0.85,
            },
          }}
        >
          Learn More
        </Button>
      </Box>

      {/* Organic image column */}
      <Box
        sx={{
          flex: "1 1 48%",
          position: "relative",
          minHeight: { xs: 160, sm: "auto" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pr: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          component="img"
          src={image}
          alt={title}
          loading="lazy"
          sx={{
            width: "100%",
            height: { xs: 150, sm: 180 },
            objectFit: "cover",
            borderRadius: "42% 58% 48% 52% / 48% 42% 58% 52%",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </Box>
    </Box>
  );
};

const HomeServices = () => {
  const navigate = useNavigate();
  const theme = useSelector((s) => s.themeValue?.colors) || {};
  const accent =
    theme.primary_gradient_end_color ||
    theme.primary_gradient_start_color ||
    "#7B1FA2";

  const goContact = () => navigate("/contact");

  return (
    <Box
      id="services"
      sx={{
        position: "relative",
        bgcolor: "#F7F6F9",
        py: { xs: 5, md: 8 },
        px: { xs: 2, sm: 3 },
        overflow: "hidden",
        scrollMarginTop: { xs: "84px", sm: "96px" },
      }}
    >
      {/* Soft skyline watermark */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: { xs: -40, md: 20 },
          bottom: 0,
          width: { xs: 220, md: 360 },
          height: { xs: 80, md: 120 },
          opacity: 0.07,
          background: `linear-gradient(0deg, ${accent}, transparent)`,
          clipPath:
            "polygon(0% 100%, 8% 70%, 18% 85%, 28% 45%, 38% 65%, 48% 30%, 58% 55%, 68% 20%, 78% 50%, 88% 35%, 100% 60%, 100% 100%)",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ maxWidth: 1140, mx: "auto", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Stack alignItems="center" sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 36, height: 2, bgcolor: accent, borderRadius: 1 }} />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: accent,
              }}
            >
              Our Services
            </Typography>
            <Box sx={{ width: 36, height: 2, bgcolor: accent, borderRadius: 1 }} />
          </Stack>

          <Typography
            align="center"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2rem" },
              color: "#1A1A2E",
              lineHeight: 1.3,
              maxWidth: 640,
              mb: 1.25,
              px: 1,
            }}
          >
            End-to-end real-estate support tailored to your goals.
          </Typography>

          <Typography
            align="center"
            sx={{
              color: "#6B7280",
              fontSize: { xs: 14, sm: 15 },
              maxWidth: 560,
              lineHeight: 1.6,
              px: 1,
            }}
          >
            From buying and selling to construction and beyond, we provide
            complete real estate solutions under one roof.
          </Typography>
        </Stack>

        {/* 3 + 2 centered grid */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2.5,
          }}
        >
          {SERVICES.map((service, index) => (
            <Box
              key={service.title}
              sx={{
                width: {
                  xs: "100%",
                  sm: "calc(50% - 10px)",
                  md: "calc(33.333% - 14px)",
                },
                maxWidth: { md: 360 },
                display: "flex",
              }}
            >
              <ServiceCard
                service={service}
                index={index}
                accent={accent}
                onLearnMore={goContact}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomeServices;
